import React, { useState, useEffect } from 'react';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Database,
  Zap,
  RefreshCw,
  AlertTriangle,
  Settings,
  RotateCcw,
  Search,
  Shield
} from 'lucide-react';
import { lambdaEndpoints } from '../../aws-config';

interface UserData {
  user_id: string;
  email?: string;
  tier: string;
  storage_gb: number;
  queries_today: number;
  queries_month: number;
  queries_alltime: number;
}

interface SystemStats {
  total_users: number;
  users_by_tier: Record<string, number>;
  storage: {
    total_original_gb: number;
    total_compressed_gb: number;
  };
  queries: {
    today: number;
    this_month: number;
    alltime: number;
  };
  timestamp: string;
}

interface UserLimits {
  tier: string;
  storage_limit_gb: number;
  query_limit_daily: number;
  query_limit_monthly: number;
  notes?: string;
}

// Admin email list - should match backend
const ADMIN_EMAILS = ['admin@bigsplice.com', 'admin@bigsplicer.com', 'lmdlogistics@outlook.com'];

export const AdminPage: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [editLimitsDialogOpen, setEditLimitsDialogOpen] = useState(false);
  const [resetCountersDialogOpen, setResetCountersDialogOpen] = useState(false);
  const [userLimits, setUserLimits] = useState<UserLimits>({
    tier: 'free',
    storage_limit_gb: 5,
    query_limit_daily: 100,
    query_limit_monthly: 1000,
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const attributes = await fetchUserAttributes();
      const email = attributes.email || '';

      if (ADMIN_EMAILS.includes(email.toLowerCase())) {
        setIsAdmin(true);
        await loadData();
      } else {
        setIsAdmin(false);
      }
    } catch (err) {
      console.error('Error checking admin access:', err);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    // Check if admin API is configured
    if (!lambdaEndpoints.adminApi) {
      // Generate mock data
      const mockStats: SystemStats = {
        total_users: 25,
        users_by_tier: { free: 20, pro: 4, enterprise: 1 },
        storage: { total_original_gb: 15.5, total_compressed_gb: 5.2 },
        queries: { today: 150, this_month: 3500, alltime: 25000 },
        timestamp: new Date().toISOString(),
      };

      const mockUsers: UserData[] = [
        { user_id: 'user-123', tier: 'pro', storage_gb: 2.5, queries_today: 25, queries_month: 450, queries_alltime: 2500 },
        { user_id: 'user-456', tier: 'free', storage_gb: 0.8, queries_today: 10, queries_month: 120, queries_alltime: 800 },
        { user_id: 'user-789', tier: 'enterprise', storage_gb: 8.2, queries_today: 50, queries_month: 1200, queries_alltime: 15000 },
        { user_id: 'user-abc', tier: 'free', storage_gb: 1.2, queries_today: 5, queries_month: 80, queries_alltime: 450 },
        { user_id: 'user-def', tier: 'pro', storage_gb: 3.1, queries_today: 35, queries_month: 600, queries_alltime: 3200 },
      ];

      setStats(mockStats);
      setUsers(mockUsers);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) throw new Error('Not authenticated');

      // Fetch stats
      const statsResponse = await fetch(`${lambdaEndpoints.adminApi}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.success) setStats(statsData.stats);
      }

      // Fetch users
      const usersResponse = await fetch(`${lambdaEndpoints.adminApi}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        if (usersData.success) setUsers(usersData.users);
      }
    } catch (err: any) {
      console.error('Error loading admin data:', err);
      toast.error('Failed to load admin data');
    }
  };

  const handleUpdateLimits = async () => {
    if (!selectedUser) return;

    // Check if admin API is configured
    if (!lambdaEndpoints.adminApi) {
      toast.success('Limits updated (demo mode)');
      setEditLimitsDialogOpen(false);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await fetch(`${lambdaEndpoints.adminApi}/admin/users/${selectedUser}/limits`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userLimits),
      });

      if (response.ok) {
        toast.success('Limits updated successfully');
        setEditLimitsDialogOpen(false);
        await loadData();
      } else {
        throw new Error('Failed to update limits');
      }
    } catch (err: any) {
      toast.error('Failed to update limits');
    }
  };

  const handleResetCounters = async (counterType: string) => {
    if (!selectedUser) return;

    // Check if admin API is configured
    if (!lambdaEndpoints.adminApi) {
      toast.success(`Counters reset (demo mode): ${counterType}`);
      setResetCountersDialogOpen(false);
      return;
    }

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await fetch(`${lambdaEndpoints.adminApi}/admin/users/${selectedUser}/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: counterType }),
      });

      if (response.ok) {
        toast.success(`${counterType} counters reset successfully`);
        setResetCountersDialogOpen(false);
        await loadData();
      } else {
        throw new Error('Failed to reset counters');
      }
    } catch (err: any) {
      toast.error('Failed to reset counters');
    }
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case 'pro':
        return 'bg-blue-500';
      case 'enterprise':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const filteredUsers = users.filter((user) =>
    user.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <Shield className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold">Access Denied</h2>
            <p className="text-muted-foreground">You do not have admin access.</p>
            <Link to="/dashboard">
              <Button>Return to Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-muted-foreground">Manage users and view system statistics</p>
            </div>
          </div>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* System Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold">{stats.total_users}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Storage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold">{stats.storage.total_original_gb.toFixed(2)} GB</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.storage.total_compressed_gb.toFixed(2)} GB compressed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Queries Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold">{stats.queries.today.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Queries This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <span className="text-2xl font-bold">{stats.queries.this_month.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tier Distribution */}
        {stats && stats.users_by_tier && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Users by Tier</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 flex-wrap">
                {Object.entries(stats.users_by_tier).map(([tier, count]) => (
                  <div key={tier} className="flex items-center gap-2">
                    <Badge className={getTierBadgeColor(tier)}>{tier.toUpperCase()}</Badge>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Management
            </CardTitle>
            <CardDescription>View and manage user limits</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="flex items-center gap-2 mb-4">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user ID or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>

            {/* Users Table */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User ID</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Storage</TableHead>
                    <TableHead className="text-right">Today</TableHead>
                    <TableHead className="text-right">Month</TableHead>
                    <TableHead className="text-right">All Time</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell className="font-mono text-sm">
                          {user.user_id.length > 20
                            ? `${user.user_id.slice(0, 8)}...${user.user_id.slice(-8)}`
                            : user.user_id
                          }
                        </TableCell>
                        <TableCell className="text-sm">
                          {user.email || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={getTierBadgeColor(user.tier)}>{user.tier.toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{user.storage_gb.toFixed(2)} GB</TableCell>
                        <TableCell className="text-right">{user.queries_today}</TableCell>
                        <TableCell className="text-right">{user.queries_month}</TableCell>
                        <TableCell className="text-right">{user.queries_alltime.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user.user_id);
                                setUserLimits({
                                  tier: user.tier,
                                  storage_limit_gb: 5,
                                  query_limit_daily: 100,
                                  query_limit_monthly: 1000,
                                });
                                setEditLimitsDialogOpen(true);
                              }}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user.user_id);
                                setResetCountersDialogOpen(true);
                              }}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Limits Dialog */}
        <Dialog open={editLimitsDialogOpen} onOpenChange={setEditLimitsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User Limits</DialogTitle>
              <DialogDescription>
                Update limits for user: {selectedUser?.slice(0, 20)}...
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tier</Label>
                <Select
                  value={userLimits.tier}
                  onValueChange={(value) => setUserLimits({ ...userLimits, tier: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Storage Limit (GB)</Label>
                <Input
                  type="number"
                  value={userLimits.storage_limit_gb}
                  onChange={(e) => setUserLimits({ ...userLimits, storage_limit_gb: parseFloat(e.target.value) })}
                />
              </div>

              <div className="space-y-2">
                <Label>Daily Query Limit</Label>
                <Input
                  type="number"
                  value={userLimits.query_limit_daily}
                  onChange={(e) => setUserLimits({ ...userLimits, query_limit_daily: parseInt(e.target.value) })}
                />
              </div>

              <div className="space-y-2">
                <Label>Monthly Query Limit</Label>
                <Input
                  type="number"
                  value={userLimits.query_limit_monthly}
                  onChange={(e) => setUserLimits({ ...userLimits, query_limit_monthly: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditLimitsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateLimits}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Counters Dialog */}
        <Dialog open={resetCountersDialogOpen} onOpenChange={setResetCountersDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset User Counters</DialogTitle>
              <DialogDescription>
                Choose which counters to reset for user: {selectedUser?.slice(0, 20)}...
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2 py-4">
              <Button variant="outline" onClick={() => handleResetCounters('daily')}>
                Reset Daily Counters
              </Button>
              <Button variant="outline" onClick={() => handleResetCounters('monthly')}>
                Reset Monthly Counters
              </Button>
              <Button variant="outline" onClick={() => handleResetCounters('storage')}>
                Reset Storage Counters
              </Button>
              <Button variant="destructive" onClick={() => handleResetCounters('all')}>
                Reset All Counters
              </Button>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setResetCountersDialogOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Last Updated */}
        {stats && (
          <p className="text-xs text-center text-muted-foreground mt-6">
            Last updated: {new Date(stats.timestamp).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
};
